import React, { useContext } from 'react'

import { ModalActionTypes, Spinner } from '@acorns/web-components'
import {
  AuthContext,
  AuthContextProvider,
  AuthType,
  FlowBuilder,
  FlowBuilderOptions,
  MFAPageSkeleton,
  PageType,
  helpPrompt,
} from '@acorns/web-mfa'
import { useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import Nav from 'components/Nav'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { PromptProps, withPrompt } from 'utils/prompts'

import {
  AuthConfirmEvent,
  ChallengeInitiatedEvent,
  CodeSubmittedEvent,
  HelpButtonTapped,
  HelpModalContainerViewed,
  HelpModalCotactSupportButtonTapped,
  HelpModalOkButtonTapped,
  MfaChallengeScreenViewedEvent,
  MfaVerifyScreenViewedEvent,
  NextButtonTapped,
} from './analytics'
import { Page } from './components'
import userQuery from './userQuery.gql'

type InnerProps = WithFlowRouterProps & WithOptimizelyProps & PromptProps

const enhance = compose<InnerProps, {}>(withOptimizely, withFlowRouter, withPrompt)

const MfaSetupFlow = ({ route }) => {
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { data } = useQuery(userQuery, {
    fetchPolicy: 'network-only',
  })

  const pages: MFAPageSkeleton[] = [
    {
      type: PageType.CHALLENGE,
      onLoaded: () => {
        trackEvent('MfaChallengePageViewed', { eventData: MfaChallengeScreenViewedEvent })
      },
      onContinue: () => {
        trackEvent('NextButtonTapped', { eventData: NextButtonTapped })
        trackEvent('CodeSubmittedTapped', { eventData: CodeSubmittedEvent })
      },
      onHelp: () => {
        trackEvent('Button Tapped', { eventData: HelpButtonTapped })
      },
      onSuccess: () => {
        trackEvent('MfaChallengeInitiated', { eventData: ChallengeInitiatedEvent })
      },
    },
    {
      type: PageType.VERIFY,
      onLoaded: () => {
        trackEvent('MfaVerifyPageViewed', { eventData: MfaVerifyScreenViewedEvent })
      },
      onContinue: () => {
        trackEvent('NextButtonTapped', { eventData: NextButtonTapped })
      },
      onHelp: () => {
        trackEvent('Button Tapped', { eventData: HelpButtonTapped })
      },
      onSuccess: () => {
        trackEvent('Button Tapped', { eventData: AuthConfirmEvent })
        isDynamicFunnelEnabled
          ? routeToPage(PageName.MfaSetUp, ActionType.Continue)
          : route({ from: FlowRoute.MFASetup, action: 'continue' })
      },
    },
  ]

  if (data) {
    const defaultPhoneNumber = data?.user?.phoneNumber
    const options: FlowBuilderOptions = {
      mode: AuthType.Phone,
      modeSwitchOn: true,
      showHelpOnChallengePage: false,
      ...(defaultPhoneNumber && { defaultPhoneNumber }),
    }
    return <FlowBuilder pages={pages} options={options} />
  } else {
    return <Spinner />
  }
}

const MfaSetupCore = enhance(({ promptUser, route }) => {
  const { isBackEnabled, isDynamicFunnelEnabled } = useContext(FunnelContext)
  const { authMode } = useContext(AuthContext)

  const handleHelp = async () => {
    trackEvent('Button Tapped', { eventData: HelpButtonTapped })

    const config = helpPrompt({ mode: authMode })

    const openModalCallback = () =>
      trackEvent('Container Viewed', { eventData: HelpModalContainerViewed })

    await promptUser({ ...config, openModalCallback }).then(res => {
      if (res?.type === ModalActionTypes.primary) {
        trackEvent('Button Tapped', { eventData: HelpModalCotactSupportButtonTapped })
      } else if (res?.type === ModalActionTypes.secondary) {
        trackEvent('Button Tapped', { eventData: HelpModalOkButtonTapped })
      }

      res?.handler()
    })
  }

  return (
    <>
      <Nav
        back={isDynamicFunnelEnabled ? isBackEnabled(PageName.MfaSetUp) : true}
        backClickOriginEventData={true}
        backClickTracker={TRACKER_IDS.MFA.BACK_BUTTON}
        linkText="Help"
        onLink={handleHelp}
      />
      <Page>
        <MfaSetupFlow route={route} />
      </Page>
    </>
  )
})

const MfaSetup = () => (
  <AuthContextProvider>
    <MfaSetupCore />
  </AuthContextProvider>
)

export default MfaSetup
